import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  Backdrop,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { updateMoveService } from "../redux/action/move-service";

interface EditMoveDateModalProps {
  open: boolean;
  onClose: () => void;
  currentDate: string;
  canonicalId: string;
  cancellationPeriod?: number | null; // Add cancellationPeriod prop
  onUpdateSuccess?: () => void;
}

const EditMoveDateModal = ({
  open,
  onClose,
  currentDate,
  canonicalId,
  cancellationPeriod,
  onUpdateSuccess,
}: EditMoveDateModalProps) => {
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState("");

  // Check if modification is allowed based on cancellation period
  const canModifyDate = () => {
    if (
      !currentDate ||
      cancellationPeriod === null ||
      cancellationPeriod === undefined
    ) {
      return true;
    }

    const moveDate = dayjs(currentDate);
    const today = dayjs().startOf("day");
    const daysDiff = moveDate.diff(today, "day");

    return daysDiff > cancellationPeriod;
  };
  useEffect(() => {
    if (open && currentDate) {
      const tomorrow = dayjs().add(1, "day");
      const initialDate = dayjs(currentDate).isBefore(tomorrow, "day")
        ? tomorrow
        : dayjs(currentDate);
      setSelectedDate(initialDate);
    }
  }, [open, currentDate]);

  const handleSave = async () => {
    if (!selectedDate) return;

    try {
      setIsLoading(true);
      setLoadingMessage("Updating Date......");
      setError(null);

      // Check if modification is allowed
      if (!canModifyDate()) {
        setError(
          `Move date cannot be modified within ${cancellationPeriod} days of the move date.`
        );
        setIsLoading(false);
        return;
      }

      setTimeout(async () => {
        try {
          const updatedData = {
            canonical_id: canonicalId,
            moving_date: selectedDate.toISOString(),
          };

          await dispatch<any>(updateMoveService(updatedData)).then(
            unwrapResult
          );

          if (onUpdateSuccess) onUpdateSuccess();
          onClose();
          window.location.reload();
        } catch (err: any) {
          setError(err.message || "Failed to update move date");
        } finally {
          setIsLoading(false);
        }
      }, 1500);
    } catch (error) {
      console.log(error, "error");
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={isLoading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
      >
        {isLoading && (
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
            onClick={() => {
              setIsLoading(true);
            }}
          >
            <Box sx={{ width: "50%" }}>
              <LinearProgress />
              <Typography
                fontSize={16}
                fontWeight={500}
                textAlign="center"
                mt={1}
              >
                {loadingMessage}
              </Typography>
            </Box>
          </Backdrop>
        )}
        <DialogTitle>Modify Move Date</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, opacity: isLoading ? 0.5 : 1 }}>
            <Typography variant="body1" gutterBottom>
              Current Move Date: {dayjs(currentDate).format("MMMM D, YYYY")}
            </Typography>

            {!canModifyDate() && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Move date cannot be modified within {cancellationPeriod} days of
                the move date.
              </Alert>
            )}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="New Move Date"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                shouldDisableDate={(date) =>
                  date.isBefore(dayjs().add(1, "day"), "day")
                }
                minDate={dayjs().add(1, "day")}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                  },
                }}
                disabled={isLoading || !canModifyDate()}
              />
            </LocalizationProvider>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={!selectedDate || isLoading || !canModifyDate()}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditMoveDateModal;
